// Local contrast blob detector: returns centers normalized 0..1
export function detectParts(img, maxDim = 420) {
  const k = Math.min(1, maxDim / Math.max(img.width, img.height));
  const W = Math.max(1, Math.round(img.width * k)), H = Math.max(1, Math.round(img.height * k));
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const ctx = c.getContext('2d');
  ctx.drawImage(img, 0, 0, W, H);
  const px = ctx.getImageData(0, 0, W, H).data;
  const N = W * H;
  const g = new Float32Array(N);
  for (let i = 0; i < N; i++) g[i] = 0.299 * px[i * 4] + 0.587 * px[i * 4 + 1] + 0.114 * px[i * 4 + 2];

  // local background via box blur (integral image)
  const r = Math.round(Math.max(W, H) / 12);
  const I = new Float64Array((W + 1) * (H + 1));
  for (let y = 0; y < H; y++) {
    let row = 0;
    for (let x = 0; x < W; x++) { row += g[y * W + x]; I[(y + 1) * (W + 1) + x + 1] = I[y * (W + 1) + x + 1] + row; }
  }
  const diff = new Float32Array(N);
  let sum = 0, sq = 0;
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const x0 = Math.max(0, x - r), x1 = Math.min(W, x + r + 1), y0 = Math.max(0, y - r), y1 = Math.min(H, y + r + 1);
    const s = I[y1 * (W + 1) + x1] - I[y0 * (W + 1) + x1] - I[y1 * (W + 1) + x0] + I[y0 * (W + 1) + x0];
    const d = Math.abs(g[y * W + x] - s / ((x1 - x0) * (y1 - y0)));
    diff[y * W + x] = d; sum += d; sq += d * d;
  }
  const mean = sum / N, sd = Math.sqrt(Math.max(0, sq / N - mean * mean));
  const th = mean + 1.2 * sd;
  const mask = new Uint8Array(N);
  for (let i = 0; i < N; i++) mask[i] = diff[i] > th ? 1 : 0;

  const seen = new Uint8Array(N);
  const blobs = [];
  const minA = N * 0.00025, maxA = N * 0.06;
  const stack = [];
  for (let i = 0; i < N; i++) {
    if (!mask[i] || seen[i]) continue;
    let a = 0, sx = 0, sy = 0;
    stack.push(i); seen[i] = 1;
    while (stack.length) {
      const j = stack.pop();
      const x = j % W, y = (j / W) | 0;
      a++; sx += x; sy += y;
      for (const n of [j - 1, j + 1, j - W, j + W]) {
        if (n < 0 || n >= N || seen[n] || !mask[n]) continue;
        if ((n === j - 1 && x === 0) || (n === j + 1 && x === W - 1)) continue;
        seen[n] = 1; stack.push(n);
      }
    }
    if (a >= minA && a <= maxA) blobs.push({ x: sx / a / W, y: sy / a / H, a });
  }
  return blobs.slice(0, 400);
}

export function mergeDetections(ai, blobs, radius = 0.05) {
  const used = new Set();
  return ai.map((p) => {
    let best = -1, bd = radius;
    blobs.forEach((b, i) => {
      if (used.has(i)) return;
      const d = Math.hypot(b.x - p.x, b.y - p.y);
      if (d < bd) { bd = d; best = i; }
    });
    if (best < 0) return p;
    used.add(best);
    return { ...p, x: blobs[best].x, y: blobs[best].y };
  });
}
